export type UserPersona =
  | "legacy"
  | "nmg_b2c_teacher"
  | "nmg_school_admin"
  | "professor_school_global";

export type UserTypeLabelVariant = "legacy" | "nmg";

export type UserTypeLabel = {
  key: string;
  labelKey: string;
  variant: UserTypeLabelVariant;
};

const LEGACY_ROLE_KEYS: Record<string, string> = {
  financial_admin: "userTypeTag.legacyAdmin",
  admin: "userTypeTag.legacyManager",
  secretary: "userTypeTag.legacySecretary",
  teacher: "userTypeTag.legacyTeacher",
};

const PROFESSOR_SCHOOL_GLOBAL = "professor_school_global";

export function resolveGroupsForAccount(
  user: {
    groups?: string[];
    accountMemberships?: Array<{ accountId: string; groups: string[] }>;
  } | null | undefined,
  accountId?: string | null
): string[] {
  if (!user) return [];
  if (!accountId) return user.groups || [];

  const membership = user.accountMemberships?.find(
    (entry) => String(entry.accountId) === String(accountId)
  );
  if (membership?.groups?.length) {
    return membership.groups;
  }

  return user.groups || [];
}

export function resolveUserPersona(input: {
  user?: {
    groups?: string[];
    accountMemberships?: Array<{ accountId: string; groups: string[] }>;
    _id?: string;
  } | null;
  account?: {
    _id?: string;
    owner?: string;
    "new-manager-global"?: boolean;
    signupProfile?: "teacher" | "school";
  } | null;
  persona?: UserPersona | string | null;
}): UserPersona {
  if (input.persona) {
    return input.persona as UserPersona;
  }

  const account = input.account;
  const accountId = account?._id ? String(account._id) : null;
  const groups = resolveGroupsForAccount(input.user, accountId);
  const isNmg = Boolean(account?.["new-manager-global"]);

  if (!isNmg) {
    return "legacy";
  }

  const isOwner =
    Boolean(input.user?._id && account?.owner) &&
    String(account?.owner) === String(input.user?._id);

  if (account?.signupProfile === "school") {
    if (
      groups.includes(PROFESSOR_SCHOOL_GLOBAL) ||
      (groups.includes("teacher") && !groups.some((g) => g === "admin" || g === "financial_admin"))
    ) {
      return "professor_school_global";
    }
    if (groups.some((g) => g === "admin" || g === "financial_admin") || isOwner) {
      return "nmg_school_admin";
    }
    if (groups.includes("teacher")) {
      return "professor_school_global";
    }
    return "nmg_school_admin";
  }

  if (account?.signupProfile === "teacher" || isOwner) {
    return "nmg_b2c_teacher";
  }

  if (groups.includes(PROFESSOR_SCHOOL_GLOBAL) || groups.includes("teacher")) {
    return "professor_school_global";
  }

  return "nmg_b2c_teacher";
}

export function resolveUserTypeLabel(input: {
  user?: {
    groups?: string[];
    accountMemberships?: Array<{ accountId: string; groups: string[] }>;
    _id?: string;
  } | null;
  account?: {
    _id?: string;
    owner?: string;
    "new-manager-global"?: boolean;
    signupProfile?: "teacher" | "school";
  } | null;
  persona?: UserPersona | string | null;
}): UserTypeLabel {
  const persona = resolveUserPersona(input);
  const account = input.account;
  const accountId = account?._id ? String(account._id) : null;
  const groups = resolveGroupsForAccount(input.user, accountId);
  const primaryGroup = groups[0] || input.user?.groups?.[0] || "teacher";

  if (persona === "legacy") {
    return {
      key: primaryGroup,
      labelKey: LEGACY_ROLE_KEYS[primaryGroup] || "userTypeTag.legacyTeacher",
      variant: "legacy",
    };
  }

  if (persona === "professor_school_global") {
    return {
      key: PROFESSOR_SCHOOL_GLOBAL,
      labelKey: "userTypeTag.nmgSchoolTeacher",
      variant: "nmg",
    };
  }

  if (persona === "nmg_school_admin") {
    return {
      key: "nmg_school",
      labelKey: "userTypeTag.nmgSchool",
      variant: "nmg",
    };
  }

  return {
    key: "nmg_teacher",
    labelKey: "userTypeTag.nmgTeacher",
    variant: "nmg",
  };
}

export function getUserTypeBadgeClassName(variant: UserTypeLabelVariant): string {
  if (variant === "nmg") {
    return "bg-primary/10 text-primary border-primary/20";
  }
  return "bg-muted text-muted-foreground border-border";
}
